import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import CropImage from '../crop-image/CropImage';
import HiddenFieldExplanation from '../hidden-field-description/HiddenFieldDescription';
import Image from '../image/Image';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import ButtonBase from '../button/ButtonBase';
import styles from './editable-image.module.less';

const EditableImage = ({
    className = '',
    uploadHandler,
    onDropRejected,
    cropHandler,
    removeImageHandler,
    children,
    src,
    altText,
    editable,
    tmpImage,
    placeholder,
    placeholderLoading,
    aspectRatio = 1200 / 624,
}) => {
    const [isCropping, setIsCropping] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);

    const onDropAccepted = (files) => {
        if (!files || files.length === 0) {
            return;
        }

        setLoading(true);

        const file = files[0];

        uploadHandler(file)
            .then(() => {
                setIsCropping(true);
                setLoading(false);
            })
            .catch(() => {
                setDefaultState();
            });
    };

    const setDefaultState = () => {
        setLoading(false);
        setIsCropping(false);
        setShowRemoveConfirmModal(false);
    };

    const onRejectDrop = () => {
        setDefaultState();
        onDropRejected();
    };

    const onSaveCropped = (crop) => {
        setLoading(true);

        cropHandler(crop)
            .then(() => {
                setLoading(false);
                setIsCropping(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const onRemove = () => {
        setLoading(true);

        removeImageHandler()
            .then(() => {
                setDefaultState();
            })
            .catch(() => {
                setDefaultState();
            });
    };

    const cancelCropButtonHandler = () => {
        setDefaultState();
    };

    const image = children ? children : <Image src={src} alt={altText} className={styles.image} />;

    const { getRootProps, getInputProps } = useDropzone({
        onDropRejected: onRejectDrop,
        onDropAccepted,
        multiple: false,
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/webp': [],
        },
    });

    if (!editable) {
        return src ? image : null;
    }

    const cropping = (
        <CropImage
            isOpen={isCropping && tmpImage && !!cropHandler}
            cropImageHandler={onSaveCropped}
            cancelButtonHandler={cancelCropButtonHandler}
            imageUrl={tmpImage}
            isSavingCroppedImage={loading}
            startWidth={100}
            aspect={aspectRatio}
        />
    );

    if (src) {
        return (
            <>
                <ConfirmModal
                    isOpen={showRemoveConfirmModal}
                    title={'Ta bort bildet'}
                    successText={'Fjern bildet'}
                    cancelText={'Avbryt'}
                    onClickSuccess={() => {
                        onRemove();
                        setShowRemoveConfirmModal(false);
                    }}
                    onClickCancel={() => setShowRemoveConfirmModal(false)}
                />
                <div className={[styles.editableImage, className].join(' ')}>
                    {image}
                    <div className={styles.editImageButton}>
                        <ButtonBase
                            className={styles.editableImageRemover}
                            onClick={() => {
                                setShowRemoveConfirmModal(true);
                            }}
                            disabled={loading}
                        >
                            {loading ? 'Fjerner …' : 'Fjern bildet'}
                        </ButtonBase>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <button
                {...getRootProps()}
                role={null}
                className={[styles.dropZone, className].join(' ')}
                type="button"
                aria-label="Last opp bilde"
            >
                <input {...getInputProps()} aria-label="Klikk her for å velge bilde du vil laste opp" />
                <div className={styles.innerDropZone}>
                    {loading ? placeholderLoading || 'Laster opp …' : placeholder}
                </div>
                <HiddenFieldExplanation htmlFor="editableImageInput" text="Legg inn bilde" />
            </button>

            {cropping}
        </>
    );
};

export default EditableImage;
